<template>
  <div>
    <template>
      <section class="h-screen font-sans bg-cover">
        <form v-if="!isCheckingToken && isValidToken" @submit="saveNewPassword()"  v-on:submit.prevent="saveNewPassword()" class="container mx-auto h-full flex flex-1 justify-center items-center">
          <div class="max-w-sm w-96 m-4 p-10 bg-white rounded shadow-xl">

            <div class="content">
              <h2 class="mb-4 font-bold text-xl">Reset your password</h2>

              <input v-model="password" @input="checkPassword" type="password" placeholder="Enter a new password" class="mb-2 p-3 placeholder-gray-400 border border-gray-300 text-gray-700 relative bg-white rounded text-sm outline-none focus:outline-none focus:shadow-outline w-full">

              <div class="w-full password-complexity mb-2 flex">
                <div class="w-1/3 rounded h-1 bg-gray-400 mr-2" :class="{'bg-red-600': scorePassword > 10, 'bg-yellow-600': scorePassword > 40, 'bg-green-600': scorePassword > 60}"></div>
                <div class="w-1/3 rounded h-1 bg-gray-400 mr-2" :class="{'bg-yellow-600': scorePassword > 40, 'bg-green-600': scorePassword > 60}"></div>
                <div class="w-1/3 rounded h-1 bg-gray-400" :class="{'bg-green-600': scorePassword > 60}"></div>
              </div>

              <input v-model="passwordConfirmation" type="password" placeholder="Confirm the password" class="mb-4 p-3 placeholder-gray-400 border border-gray-300 text-gray-700 relative bg-white rounded text-sm outline-none focus:outline-none focus:shadow-outline w-full">
            </div>
            <footer>

              <div class="text-red-500 pb-1">{{ text_failure }}</div>
              <button @click="saveNewPassword" type="submit" class="px-8 py-3 rounded bg-green-500 text-white w-full shadow-sm disabled:opacity-50" >{{ $t('auth.save-password') }}</button>
            </footer>
            <br>
            <span class="py-3 text-gray-500 text-base">{{$t('auth.password-check')}}</span>
            <div class="mt-1">
              <router-link :to="{ name: 'forgot-password' }" class="text-green-800 font-bold">{{ $t('auth.forgot-password') }}</router-link>
            </div>
          </div>

          <h1 class="font-logo text-4xl ml-10"><span class="text-gray-600">Rocket</span><span class="text-primary">fid</span></h1>
        </form>

        <form v-on:submit.prevent v-if="!isCheckingToken && !isValidToken" class="container mx-auto h-full flex flex-1 justify-center items-center">
          <div class="max-w-sm w-96 m-4 p-10 bg-white rounded shadow-xl">

            <div class="content">
              <h2 class="mb-4 font-bold text-xl">Sorry, your password reset link expired</h2>
              <p class="mb-4">Please try again</p>
              <input @keypress="cleanError($event.target)" v-model="email" required placeholder="Email" v-bind:class="{ 'mb-1 p-3 placeholder-gray-400 border border-gray-300 text-gray-700 relative bg-white rounded text-sm outline-none focus:outline-none focus:shadow-outline w-full': msgErrorMail, 'mb-6 p-3 placeholder-gray-400 border border-gray-300 text-gray-700 relative bg-white bg-white rounded text-sm outline-none focus:outline-none focus:shadow-outline w-full': !msgErrorMail }">
              <p class="text-red-500 pb-1">{{ msgErrorMail ? $t('auth.invalid-mail') : ''}}</p>
            </div>
            <footer>
              <common-button
                @click="forgot"
                type="primary"
                :submit="true"
                :is-loading="isLoading"
                :full-width="true"
                :disabled="isProcessing || !email">{{ $t('auth.ask-new-password') }}</common-button>
            </footer>

            <div class="mt-1">
              <router-link :to="{ name: 'signin' }" class="text-green-800 font-bold">Go to {{ $t('auth.signin') }}</router-link>
            </div>
          </div>

          <h1 class="font-logo text-4xl ml-10"><span class="text-gray-600">Rocket</span><span class="text-primary">fid</span></h1>
        </form>

        <div v-if="isCheckingToken" class="container mx-auto h-full text-center justify-center items-center">
          <common-ui-loader />
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ResetPassword',
  data () {
    return {
      email: null,
      password: null,
      passwordConfirmation: null,
      token: null,
      isLoading: false,
      isProcessing: false,
      isValidToken: false,
      isCheckingToken: true,
      EMAIL_REGEX: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      msgErrorMail: false,
      resetPassword: true,
      errorCode: '',
      text_failure: ''
    }
  },
  computed: {
    scorePassword () {
      let score = 0
      if (!this.password) { return score }

      // award every unique letter until 5 repetitions
      let letters = {}
      for (let i = 0; i < this.password.length; i++) {
        letters[this.password[i]] = (letters[this.password[i]] || 0) + 1
        score += 5.0 / letters[this.password[i]]
      }

      // bonus points for mixing it up
      let variations = {
        digits: /\d/.test(this.password),
        lower: /[a-z]/.test(this.password),
        upper: /[A-Z]/.test(this.password),
        nonWords: /\W/.test(this.password)
      }

      let variationCount = 0
      for (let check in variations) {
        variationCount += (variations[check] === true) ? 1 : 0
      }
      score += (variationCount - 1) * 10
      return parseInt(score)
    }
  },
  methods: {
    saveNewPassword () {
      if (this.checkPassword() === null) {
        return null
      }
      // Check if password1 === password2
      if (this.password !== this.passwordConfirmation) {
        this.text_failure = 'Your password and your confirm password are different'
        return null
      }

      if (!this.isProcessing) {
        this.isProcessing = true
        let user = {
          newpassword: this.password
        }
        this.$store.dispatch('user/resetPassword', {
          user: user,
          token: this.token
        })
          .then(() => {
            this.$notify({title: 'Success', message: this.$root.$t('notifications.user.passwordEdited'), type: 'success'})
            this.$router.push('/signin')
          }).catch((response) => {
            this.errorCode = response.code
            this.$notify.error({
              title: this.$root.$t('api.' + this.errorCode + '.title'),
              message: this.$root.$t('api.' + this.errorCode + '.message')
            })
          }).then(() => {
            this.isProcessing = false
          })
      }
    },
    checkPassword () {
      // Check if there are number, lowercase or upper
      let variations = {
        digits: /\d/.test(this.password),
        lowercase: /[a-z]/.test(this.password),
        uppercase: /[A-Z]/.test(this.password)
      }
      if (this.password === null || this.password === '') {
        this.text_failure = ''
        return null
      }
      for (let check in variations) {
        if (variations[check] === false) {
          this.text_failure = 'Oops, ' + check + ' is missing in your password'
          return null
        }
      }
      // Check if password < 8
      if (this.password && this.password.length < 8) {
        this.text_failure = 'Oops, your password must contain more than 8 characters'
        return null
      } else {
        this.text_failure = ''
        return true
      }
    },
    cleanError (elem) {
      this.isProcessing = false
      this.msgErrorMail = false
    },
    validateEmail () {
      if (this.EMAIL_REGEX.test(this.email)) {
        this.isProcessing = false
        this.msgErrorMail = false
      } else {
        this.isProcessing = true
        this.msgErrorMail = true
      }
    },
    forgot () {
      this.validateEmail()
      if (!this.msgErrorMail) {
        if (!this.isProcessing && !this.isLoading) {
          this.isProcessing = true
          this.isLoading = true
          this.$store.dispatch('user/rememberPassword', this.email)
            .then(() => {
              this.$notify({title: 'Success', message: this.$root.$t('notifications.user.passwordSent'), type: 'success'})
            }).catch((response) => {
              if (response.code === '21' && this.resetPassword) {
                this.errorCode = 25
              } else {
                this.errorCode = response.code
              }
              this.$notify.error({
                title: this.$root.$t('api.' + this.errorCode + '.title'),
                message: this.$root.$t('api.' + this.errorCode + '.message')
              })
            }).then(() => {
              this.isProcessing = false
              this.isLoading = false
            })
        }
      }
    }
  },
  mounted () {
    this.isCheckingToken = true

    // Logout user before
    this.token = this.$route.query.confirmcode

    setTimeout(() => {
      axios.get(process.env.API + '/user/', {
        headers: {
          'X-Token': this.token
        }
      }).then(() => {
        this.isValidToken = true
        this.isCheckingToken = false
      }).catch(() => {
        this.isValidToken = false
        this.isCheckingToken = false
      })
    }, 1400)
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/Auth/ResetPassword.vue